import { useState } from 'react';
import { ChevronDown, ChevronRight, Mail, Phone, Linkedin, CircleUser as UserCircle } from 'lucide-react';
import type { ContactWithAccount } from '../../types';

interface AccountGroup {
  accountId: string;
  accountName: string;
  tier: string;
  contacts: ContactWithAccount[];
}

interface Props {
  groups: AccountGroup[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  onContactClick: (contact: ContactWithAccount) => void;
  totalCount: number;
  highlightedContactIds?: Set<string>;
}

const TIER_COLORS: Record<string, string> = {
  A: 'bg-forest-100 text-forest-700 border-forest-200',
  B: 'bg-app-warning/10 text-app-warning border-app-warning',
  C: 'bg-app-canvas text-app-muted border-app-line',
};

const SENIORITY_LABELS: Record<string, string> = {
  executive: 'Exec',
  executive_leadership: 'Exec Lead',
  senior_leadership: 'Sr Lead',
  department_leader: 'Dept Lead',
  team_manager: 'Manager',
  individual_contributor: 'IC',
};

function getOutreachDisplay(contact: ContactWithAccount): { bg: string; text: string; label: string } {
  if (!contact.last_contacted_at) {
    return { bg: 'bg-app-canvas', text: 'text-app-muted', label: 'None' };
  }
  const diffMs = Date.now() - new Date(contact.last_contacted_at).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return { bg: 'bg-app-positive/10', text: 'text-app-positive', label: 'Today' };
  if (days === 1) return { bg: 'bg-app-positive/10', text: 'text-app-positive', label: '1 day ago' };
  if (days <= 7) return { bg: 'bg-app-positive/10', text: 'text-app-positive', label: `${days}d ago` };
  if (days <= 14) return { bg: 'bg-app-warning/10', text: 'text-app-warning', label: `${days}d ago` };
  if (days <= 30) return { bg: 'bg-app-warning/10', text: 'text-app-warning', label: `${days}d ago` };
  return { bg: 'bg-app-critical/10', text: 'text-app-critical', label: `${days}d ago` };
}

export default function ContactsTable({ groups, selectedIds, onToggleSelect, onToggleSelectAll, allSelected, onContactClick, totalCount, highlightedContactIds }: Props) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (accountId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-app-line bg-app-canvas/80">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected && totalCount > 0}
                  onChange={onToggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-app-line-strong text-forest-600 focus:ring-forest-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider">Role</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider hidden lg:table-cell">Seniority</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider hidden md:table-cell">Department</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider hidden xl:table-cell">Contact</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider">Assigned To</th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider">Outreach</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-app-line">
            {groups.map(group => {
              const isCollapsed = collapsedGroups.has(group.accountId);
              const tierColor = TIER_COLORS[group.tier] || TIER_COLORS.C;

              return (
                <GroupRows
                  key={group.accountId}
                  group={group}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleGroup(group.accountId)}
                  tierColor={tierColor}
                  selectedIds={selectedIds}
                  onToggleSelect={onToggleSelect}
                  onContactClick={onContactClick}
                  highlightedContactIds={highlightedContactIds}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {groups.length === 0 && (
        <div className="py-16 text-center">
          <UserCircle className="w-12 h-12 text-app-line-strong mx-auto mb-3" />
          <p className="text-sm font-medium text-app-muted">No contacts found</p>
          <p className="text-xs text-app-muted mt-1">Adjust your filters or upload contacts to get started</p>
        </div>
      )}
    </div>
  );
}

function GroupRows({ group, isCollapsed, onToggle, tierColor, selectedIds, onToggleSelect, onContactClick, highlightedContactIds }: {
  group: AccountGroup;
  isCollapsed: boolean;
  onToggle: () => void;
  tierColor: string;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onContactClick: (contact: ContactWithAccount) => void;
  highlightedContactIds?: Set<string>;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="bg-app-canvas/50 cursor-pointer hover:bg-app-canvas/50 transition-colors"
      >
        <td colSpan={8} className="px-4 py-2.5">
          <div className="flex items-center gap-2.5">
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-app-muted" />
            ) : (
              <ChevronDown className="w-4 h-4 text-app-muted" />
            )}
            <span className="text-sm font-semibold text-app-ink">{group.accountName}</span>
            {group.tier && (
              <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${tierColor}`}>
                Tier {group.tier}
              </span>
            )}
            <span className="text-xs text-app-muted">{group.contacts.length} contacts</span>
          </div>
        </td>
      </tr>
      {!isCollapsed && group.contacts.map(contact => (
        <ContactRow
          key={contact.id}
          contact={contact}
          isSelected={selectedIds.has(contact.id)}
          isHighlighted={highlightedContactIds?.has(contact.id) || false}
          onToggleSelect={() => onToggleSelect(contact.id)}
          onClick={() => onContactClick(contact)}
        />
      ))}
    </>
  );
}

function ContactRow({ contact, isSelected, isHighlighted, onToggleSelect, onClick }: {
  contact: ContactWithAccount;
  isSelected: boolean;
  isHighlighted: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
}) {
  const status = getOutreachDisplay(contact);
  const seniorityLabel = SENIORITY_LABELS[contact.seniority] || contact.seniority || 'N/A';

  return (
    <tr className={`hover:bg-forest-50/30 transition-colors group ${isHighlighted ? 'bg-forest-50/20' : ''}`}>
      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-3.5 h-3.5 rounded border-app-line-strong text-forest-600 focus:ring-forest-500"
        />
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={onClick}>
        <div className="flex items-center gap-2.5">
          <div className="relative w-8 h-8 rounded-full bg-forest-100 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-forest-700">
              {contact.first_name?.[0]}{contact.last_name?.[0]}
            </span>
            {isHighlighted && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-forest-500 border-2 border-white" />}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-app-ink truncate group-hover:text-forest-700 transition-colors">
              {contact.first_name} {contact.last_name}
            </p>
            {contact.email && (
              <p className="text-[11px] text-app-muted truncate">{contact.email}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={onClick}>
        <p className="text-sm text-app-ink truncate max-w-[200px]">{contact.role || 'N/A'}</p>
      </td>
      <td className="px-4 py-3 hidden lg:table-cell cursor-pointer" onClick={onClick}>
        <span className="text-xs font-medium text-app-muted">{seniorityLabel}</span>
      </td>
      <td className="px-4 py-3 hidden md:table-cell cursor-pointer" onClick={onClick}>
        <span className="text-xs text-app-muted">{contact.department || 'N/A'}</span>
      </td>
      <td className="px-4 py-3 hidden xl:table-cell" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="p-1 rounded hover:bg-app-canvas transition-colors" title={contact.email}>
              <Mail className="w-3.5 h-3.5 text-app-muted hover:text-forest-600" />
            </a>
          )}
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="p-1 rounded hover:bg-app-canvas transition-colors" title={contact.phone}>
              <Phone className="w-3.5 h-3.5 text-app-muted hover:text-forest-600" />
            </a>
          )}
          {contact.linkedin_url && (
            <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-app-canvas transition-colors">
              <Linkedin className="w-3.5 h-3.5 text-app-muted hover:text-app-focus" />
            </a>
          )}
        </div>
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={onClick}>
        <span className="text-xs text-app-muted">{contact.assigned_user_name || 'Unassigned'}</span>
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={onClick}>
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${status.bg} ${status.text}`}>
          {status.label}
        </span>
      </td>
    </tr>
  );
}

export type { AccountGroup };
